/* global ol */
import ExtremeMapLandmarkLayerManager from '@/lib/ExtremeMapLandmarkLayerManager'

export default class ChiseiKilopostLayerManager extends ExtremeMapLandmarkLayerManager {
  constructor(opts = {}) {
    super()
    this.colorMapDefault = [
      { line: { stroke: '#bb00bb' } },
      { line: { stroke: '#00bb00' } },
      { line: { stroke: '#0000bb' } },
      { line: { stroke: '#bbbb00' } },
      { line: { stroke: '#00bbbb' } },
      { line: { stroke: '#bb0000' } },
      { line: { stroke: '#4400bb' } },
      { line: { stroke: '#44bb00' } },
      { line: { stroke: '#0044bb' } },
      { line: { stroke: '#bb4400' } },
      { line: { stroke: '#00bb44' } },
      { line: { stroke: '#bb0044' } },
    ]
    this.colorMapPale = [
      { line: { stroke: '#d666e3' } },
      { line: { stroke: '#66d666' } },
      { line: { stroke: '#6666d6' } },
      { line: { stroke: '#d6e366' } },
      { line: { stroke: '#66d6e3' } },
      { line: { stroke: '#d66666' } },
      { line: { stroke: '#a17fdd' } },
      { line: { stroke: '#a1dd7f' } },
      { line: { stroke: '#7fa1dd' } },
      { line: { stroke: '#dda17f' } },
      { line: { stroke: '#7fdda1' } },
      { line: { stroke: '#dd7fa1' } },
    ]
  }

  getLineColorMap(groupName, colorNum) {
    let ret
    if (groupName === 'geo_connection') {
      ret = { line: { stroke: '#4286f4' } }
    } else if (groupName === 'ramp') {
      const cnt = this.colorMapPale.length
      ret = this.colorMapPale[colorNum % cnt]
    } else {
      const cnt = this.colorMapDefault.length
      ret = this.colorMapDefault[colorNum % cnt]
    }
    return ret
  }

  getPointColorMap_() {
    const fill = {
      white: 'rgba(255, 255, 255, 0.8)',
    }
    const stroke = {
      white: 'rgba(100, 100, 100, 0.8)',
    }
    return {
      point: {
        fill: Object.assign({}, fill),
        stroke: Object.assign({}, stroke),
      }
    }
  }

  getNewKilopostPointStyle_({ color, text = null }) {
    const colorMap = this.getPointColorMap_()
    const fillColor = colorMap.point.fill[color]
    const strokeColor = colorMap.point.stroke[color]
    const ret = new ol.style.Style({
      // rect
      image: new ol.style.RegularShape({
        fill: new ol.style.Fill({
          color: fillColor,
        }),
        stroke: new ol.style.Stroke({
          color: strokeColor,
          width: 1
        }),
        points: 4,
        radius: 3,
        angle: Math.PI / 4,
      })
    })
    if (text) {
      ret.setText(new ol.style.Text({
        text: text,
        font: '9px sans-serif',
        fill: new ol.style.Fill({
          color: 'black'
        }),
        stroke: new ol.style.Stroke({
          color: 'rgba(255, 255, 255, 0.8)',
          width: 2,
        }),
        offsetX: 0,
        offsetY: 8,
      }))
    }
    return ret
  }

  getPointTextFeature_(point, info) {
    const feat = new ol.Feature({
      geometry: new ol.geom.Point(point)
    })
    const style = this.getNewKilopostPointStyle_({
      color: info.defaultColor,
      text: info.text,
    })
    style.setZIndex(info.zIndex++)
    feat.setStyle(style)
    return feat
  }

  getNewAnglePointStyle_({ angle }) {
    const ret = [
      new ol.style.Style({
        image: new ol.style.Icon({
          src: '/static/img/arrow.png',
          scale: 0.02,
          rotation: angle * Math.PI / 180,
        })
      })
    ]
    return ret
  }

  getAngleArrowFeature_(point, angle, info) {
    const feat = new ol.Feature({
      geometry: new ol.geom.Point(point)
    })
    const styles = this.getNewAnglePointStyle_({ angle })
    const zIndex = info.zIndex++
    styles.forEach(e => {
      e.setZIndex(zIndex)
    })
    feat.setStyle(styles)
    return feat
  }

  getLineFeature_(coords, groupName, idx) {
    const colorMap = this.getLineColorMap(groupName, idx)
    const feat = new ol.Feature(new ol.geom.LineString(coords))
    feat.setStyle(new ol.style.Style({
      stroke: new ol.style.Stroke({ color: colorMap.line.stroke, width: 4 }),
    }))
    return feat
  }

  binSearch_(arr, num) {
    // numを見つける. arrは昇順前提.
    let low = 0
    let high = arr.length - 1
    let mid, guess
    while (low <= high) {
      mid = parseInt((low + high) / 2)
      guess = arr[mid]
      if (Math.abs(guess.kp_calc - num) < Number.EPSILON) {
        return mid
      } else if (guess.kp_calc > num) {
        high = mid - 1
      } else {
        low = mid + 1
      }
    }
    return -1
  }

  findKpsFromArr(arr, num1, num2) {
    if (!arr) { return [] }
    const ret = []
    // num1を見つける. arrは昇順前提.
    let idx = this.binSearch_(arr, num1)
    if (idx === -1) { return ret }

    if (num1 <= num2) {
      const len = arr.length
      while (idx < len) {
        const elem = arr[idx]
        if (elem.kp_calc > num2) { break }
        ret.push(arr[idx++])
      }
    } else {
      while (idx >= 0) {
        const elem = arr[idx]
        if (elem.kp_calc < num2) { break }
        ret.push(arr[idx--])
      }
    }
    return ret
  }

  findKpFromArr(arr, num) {
    const result = this.findKpsFromArr(arr, num, num)
    return result.length > 0 ? result[0] : null
  }

  getGeoConnectionFeats(srcArr, opts) {
    const kpGroupMap = srcArr.reduce((acc, e) => {
      acc[e.name] = e.obj; return acc
    }, {})
    // const geoConnections = require('@/data/kp_chisei_geo_connections.json')
    const geoConnections = {}
    const ret = []
    for (const [srcRoadNameDirection, conns] of Object.entries(geoConnections)) {
      if (srcRoadNameDirection.split('#').length > 2) { continue }
      for (const [dstK1, obj1] of Object.entries(conns)) {
        const kParts = dstK1.split('#')
        // 2か3のはず
        if (kParts.length === 2) {
          const kpg1 = kpGroupMap[srcRoadNameDirection]
          const kpg2 = kpGroupMap[dstK1]
          if (!kpg1 || !kpg2) { continue }

          const kp1 = this.findKpFromArr(kpg1.main_line, obj1.src_kp_end)
          const kp2 = this.findKpFromArr(kpg2.main_line, obj1.dst_kp_start)
          if (!kp1 || !kp2) { continue }
          const coords = [kp1, kp2].map(kp => {
            return this.convCoord({ lat: kp.lat, lon: kp.lon })
          })
          ret.push(this.getLineFeature_(coords, 'geo_connection', 9999))
        } else {
          const viaRoadNameDirection = `${kParts[0]}#${kParts[1]}`
          const viaPlaceName = kParts[2]
          const dstK2 = obj1.next_key
          const obj2 = obj1[dstK2]

          const kpg1 = kpGroupMap[srcRoadNameDirection]
          const kpg2 = kpGroupMap[viaRoadNameDirection]
          const kpg3 = kpGroupMap[dstK2]
          if (!kpg1 || !kpg2 || !kpg3) { continue }

          // 開始
          const kp1 = this.findKpFromArr(kpg1.main_line, obj1.src_kp_end)
          // 経由
          const viaKps = this.findKpsFromArr(
            kpg2[viaPlaceName], obj1.dst_kp_start, obj2.src_kp_end)
          // 終了
          const kp2 = this.findKpFromArr(kpg3.main_line, obj2.dst_kp_start)
          if (!kp1 || !kp2) { continue }
          if (viaKps.length === 0) { continue }

          const coords = [kp1, ...viaKps, kp2].map(kp => {
            return this.convCoord({ lat: kp.lat, lon: kp.lon })
          })
          ret.push(this.getLineFeature_(coords, 'geo_connection', 9999))
        }
      }
    }
    return ret
  }

  createKilopostLayer_(srcArr, opts) {
    const info = {
      zIndex: 0,
      defaultColor: 'white',
    }
    const coords = []
    const feats = []
    let mainLineCnt = 0
    let rampCnt = 0
    srcArr.forEach(kpGroup => {
      for (const ent of Object.entries(kpGroup.obj)) {
        const coordsForGroup = []
        const placeName = ent[0]
        const kps = ent[1]
        const kpsLen = kps.length
        kps.forEach((kp, idx) => {
          const p1 = this.convCoord({ lat: kp.lat, lon: kp.lon })
          coordsForGroup.push(p1)

          let showKpText = false
          let showKpAngle = false
          if (
            idx === 0 ||
            idx === kpsLen - 1 ||
            parseFloat(parseInt(kp.kp1)) === kp.kp1
          ) {
            showKpText = opts.kpTextShowType >= 1
            showKpAngle = opts.kpAngleShowType >= 1
          } else {
            showKpText = opts.kpTextShowType === 2
            showKpAngle = opts.kpAngleShowType === 2
          }

          if (showKpText) {
            const arr = [
              kp.kp_prefix,
              kp.direction,
            ]
            if (placeName !== 'main_line') {
              arr.push(kp.place_name)
            }
            if (kp.kp2 > 0) {
              // kp2があったら少数第三位まで
              arr.push(kp.kp_calc.toFixed(3))
            } else {
              // なければ普通に第二位まで
              arr.push(kp.kp_calc.toFixed(2))
            }
            info.text = arr.join(' ')
            feats.push(this.getPointTextFeature_(p1, info))
          }

          if (showKpAngle) {
            feats.push(this.getAngleArrowFeature_(p1, kp.angle, info))
          }
        })
        if (placeName === 'main_line' || placeName.match(/JCT/)) {
          feats.push(this.getLineFeature_(coordsForGroup, 'main_line', mainLineCnt++))
        } else {
          feats.push(this.getLineFeature_(coordsForGroup, 'ramp', rampCnt++))
        }
        coords.push(...coordsForGroup)
      }
    })

    feats.push(...this.getGeoConnectionFeats(srcArr, opts))

    const layer = new ol.layer.Vector({
      source: new ol.source.Vector({ features: feats }),
    })
    layer.name = this.getLayerName()
    const extent = ol.extent.boundingExtent(coords)

    this.layer = layer
    this.layerInfo.extent = extent
  }

  getLayerName() {
    return 'kilopost_chisei'
  }

  prepareLayer(srcArr, opts) {
    this.createKilopostLayer_(srcArr, opts)
    return this.getLayer()
  }
}
